<template>
  <div class="manual-page help-document-theme">
    <header class="manual-bar">
      <button type="button" aria-controls="manual-directory" :aria-expanded="String(isShowMenu)" @click="toggleDirectory">云作品说明书</button>
    </header>
    <div class="manual-layout">
      <nav v-show="isShowMenu" id="manual-directory" class="manual-directory" aria-label="说明书目录">
        <a v-for="item in listMenu" :key="item.id" :href="articleHref(item.id)" :aria-current="String(item.id) === String(id) ? 'page' : null" @click.prevent="selectArticle(item.id)">{{ item.title }}</a>
      </nav>
      <main ref="scroll" class="manual-scroll">
        <mini-program-entry v-if="miniTarget" ref="miniEntry" class="manual-mini-entry" :path="miniTarget.path" :query="miniTarget.query" />
        <div v-if="loading" class="manual-state" aria-busy="true"></div>
        <div v-else-if="!info" class="manual-state">{{ failed ? '网络异常' : '没有相关内容' }}<button v-if="failed" type="button" @click="loadArticle">重试</button></div>
        <article v-else class="manual-article">
          <header class="manual-heading"><h1>{{ info.title }}</h1><p v-if="info.category_name">{{ info.category_name }}</p></header>
          <div ref="body" class="help-reader manual-body" @click="handleLink"></div>
        </article>
      </main>
    </div>
  </div>
</template>

<script>
import { getHelpNoteDetail, getHelpNotes } from '@/request/api'
import { checkIsIOS, checkIsMobile } from '@/utils/common'
import { renderDocument, linkHref } from '@/generated/helpReader'
import '@/generated/helpReader.css'
import MiniProgramEntry from '@/components/MiniProgramEntry.vue'

export default {
  name: 'userHelpDetail',
  components: { MiniProgramEntry },
  data() {
    return { info: null, listMenu: [], loading: true, failed: false, isShowMenu: !checkIsMobile() }
  },
  computed: {
    id() { return this.$route.query.id },
    miniTarget() {
      if (!/^[1-9]\d*$/.test(this.id || '')) return null
      const destination = linkHref({ type: 'article', id: Number(this.id) }).split('?')
      return { path: destination[0].replace(/^\//, ''), query: destination[1] || '' }
    }
  },
  watch: { id() { this.loadArticle() } },
  mounted() { this._alive = true; this.loadArticle(); if (this.isShowMenu) this.loadDirectory() },
  beforeDestroy() { this._alive = false; this._generation++ },
  methods: {
    toggleDirectory() {
      this.isShowMenu = !this.isShowMenu
      if (this.isShowMenu) this.loadDirectory()
    },
    articleHref(id) { return this.$router.resolve({ name: 'userHelpDetail', query: { id: String(id) } }).href },
    selectArticle(id) {
      if (checkIsMobile()) this.isShowMenu = false
      if (String(id) !== String(this.id)) this.$router.push({ name: 'userHelpDetail', query: { id: String(id) } })
    },
    async loadArticle() {
      const generation = this._generation = (this._generation || 0) + 1
      this.loading = true
      this.failed = false
      this.info = null
      if (!/^[1-9]\d*$/.test(this.id || '')) { this.loading = false; return }
      try {
        const response = await getHelpNoteDetail({ id: this.id, format: 'document-v1' })
        if (!this._alive || generation !== this._generation) return
        const info = response.data && response.data.data
        if (!info) { this.loading = false; return }
        const structured = info.document && info.document.schema_version === 1
        if (!structured && typeof info.content !== 'string') throw new Error('Unsupported article')
        // Commit title/category/body together after the current response arrives.
        this.info = info
        this.loading = false
        await this.$nextTick()
        if (!this._alive || generation !== this._generation) return
        if (structured) {
        renderDocument(this.$refs.body, info.document, link => link.type === 'article' ? this.articleHref(link.id) : link.type === 'email' ? 'mailto:' + link.value : link.type === 'feature' ? '#' : linkHref(link))
        } else {
          // Older production returns the same article HTML used by the original reader.
          this.$refs.body.innerHTML = info.content
        }
        this.$refs.scroll.scrollTop = 0
      } catch (_) {
        if (this._alive && generation === this._generation) { this.info = null; this.failed = true; this.loading = false }
      }
    },
    async loadDirectory() {
      if (this._directoryLoading || this._directoryComplete) return
      this._directoryLoading = true
      const items = this.listMenu.slice()
      try {
        for (let page = this._directoryPage || 1; this._alive; page++) {
          const response = await getHelpNotes({ summary: 1, limit: 100, page, keywords: '', category_id: 0, isiOS: checkIsIOS() ? 1 : 0 })
          if (!this._alive) return
          const data = response.data
          if (!data || !Array.isArray(data.data)) return
          const fresh = data.data.filter(item => !items.some(existing => String(existing.id) === String(item.id)))
          items.push(...fresh.map(item => ({ id: item.id, title: Array.isArray(item.title) ? item.title.join('') : item.title })))
          this.listMenu = items.slice()
          this._directoryPage = page + 1
          if (!fresh.length || data.has_more === false || data.data.length < 100 || (data.count != null && items.length >= data.count)) { this._directoryComplete = true; break }
        }
      } catch (_) { /* Retry the failed page when the directory is reopened. */ }
      finally { this._directoryLoading = false }
    },
    handleLink(event) {
      const anchor = event.target.closest('a[data-help-link]')
      if (!anchor) return
      const link = JSON.parse(anchor.dataset.helpLink)
      if (link.type === 'article' && !event.ctrlKey && !event.metaKey && !event.shiftKey && event.button === 0) {
        event.preventDefault()
        this.selectArticle(link.id)
      } else if (link.type === 'feature') {
        event.preventDefault()
        this.$nextTick(() => {
          if (!this._alive || !this.$refs.miniEntry) return
          this.$refs.miniEntry.$el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
          this.$refs.miniEntry.open()
        })
      }
    }
  }
}
</script>

<style scoped>
.manual-page { position: fixed; inset: 0; background: var(--ui-page); color: var(--modal-text); font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
.manual-bar { height: 64px; display: flex; align-items: center; padding: 0 24px; border-bottom: 1px solid var(--ui-hairline); background: var(--ui-card-surface); }
.manual-page button { font: inherit; color: var(--modal-text); background: var(--ui-control-secondary); border: 0; border-radius: 12px; padding: 10px 16px; cursor: pointer; }
.manual-page button:focus-visible, .manual-directory a:focus-visible { outline: 2px solid var(--modal-text); outline-offset: 2px; }
.manual-layout { display: flex; height: calc(100% - 65px); }
.manual-directory { flex: 0 0 260px; overflow-y: auto; padding: 16px; background: var(--ui-card-surface); }
.manual-directory a { display: block; padding: 12px; color: var(--ui-secondary-text); text-decoration: none; font-size: 14px; line-height: 1.5; border-radius: 12px; }
.manual-directory a[aria-current=page] { color: var(--modal-text); background: var(--ui-control-secondary); font-weight: 500; }
.manual-scroll { flex: 1; min-width: 0; overflow-y: auto; }
.manual-mini-entry { padding: 20px 20px 0; }
.manual-article { max-width: 720px; margin: 0 auto; padding: 40px 20px 60px; }
.manual-heading { text-align: center; margin-bottom: 40px; }
.manual-heading h1 { font-size: 24px; line-height: 1.5; font-weight: 500; overflow-wrap: anywhere; margin: 0; }
.manual-heading p { font-size: 14px; color: var(--ui-secondary-text); margin: 12px 0 0; }
.manual-body { padding: 32px; border-radius: 20px; background: var(--ui-card-surface); }
.manual-body ::v-deep img { max-width: 100%; height: auto; }
.manual-body ::v-deep video { max-width: 100%; }
.manual-state { padding: 40px; text-align: center; font-size: 14px; }
.manual-state button { display: block; margin: 16px auto; }
@media (max-width: 720px) {
  .manual-directory { position: absolute; top: 65px; bottom: 0; left: 0; width: min(280px, 80vw); z-index: 1; }
  .manual-article { padding: 28px 10px 40px; }
  .manual-heading h1 { font-size: 17px; line-height: 26px; }
  .manual-body { padding: 20px 14px; border-radius: 16px; }
}
</style>
